import { ArticleFromApi, CategoryFromApi } from 'products/types'
import { TimeSlotGroup, TimeSlotGroupStatus } from 'timeSlots/timeSlotsService'
import { applicableGroups, categoryGroupContext, categoryTimeSlotInfo } from './applicableGroups'

export type StatusFilter = 'all' | TimeSlotGroupStatus
export type SearchKind = 'product' | 'category'

export interface SearchSelection {
    kind: SearchKind
    id: string
}

export const STATUS_VALUES: TimeSlotGroupStatus[] = ['active', 'upcoming', 'expired']

export const readStatusFilter = (value: string | undefined): StatusFilter =>
    STATUS_VALUES.includes(value as TimeSlotGroupStatus) ? (value as TimeSlotGroupStatus) : 'all'

export const readSelection = (query: { product?: string; category?: string }): SearchSelection | null => {
    if (query.product) {
        return { kind: 'product', id: query.product }
    }
    return query.category ? { kind: 'category', id: query.category } : null
}

export const legacySelection = (filter: string | undefined, productsView: boolean): SearchSelection | null => {
    if (!filter) {
        return null
    }
    const part = filter.split(';').find((entry) => entry.startsWith('products:'))
    const first = part
        ?.slice('products:'.length)
        .split(',')
        .find((id) => id)
    return first ? { kind: productsView ? 'product' : 'category', id: first } : null
}

export const effectiveStatus = (group: TimeSlotGroup, todayISO: string): TimeSlotGroupStatus => {
    if (group.status === 'expired' || (group.availableTo && group.availableTo.slice(0, 10) < todayISO)) {
        return 'expired'
    }
    if (group.availableFrom && group.availableFrom.slice(0, 10) > todayISO) {
        return 'upcoming'
    }
    return group.status
}

const idsThatApply = (
    selection: SearchSelection,
    groups: TimeSlotGroup[],
    articles: ArticleFromApi[],
    categories: CategoryFromApi[],
): string[] => {
    const info = categoryTimeSlotInfo(categories).map((category) =>
        selection.kind === 'category' && category.id === selection.id
            ? { ...category, timeSlotsEnabled: true }
            : category,
    )
    if (selection.kind === 'category') {
        return categoryGroupContext(selection.id, groups, info).groups.map((group) => group.id)
    }
    const article = articles.find((entry) => entry.id === selection.id)
    if (!article) {
        return []
    }
    const categoryIds = [...article.categories, ...(article.inactiveCategories ?? [])].map((category) => category.id)
    return applicableGroups({ id: article.id, categoryIds }, groups, info).groupIds
}

export function groupsThatApply(
    selection: SearchSelection,
    groups: TimeSlotGroup[],
    articles: ArticleFromApi[],
    categories: CategoryFromApi[],
): TimeSlotGroup[] {
    const asCurrent = groups.map((group) =>
        group.status === 'expired' ? { ...group, status: 'active' as const } : group,
    )
    const ids = new Set([
        ...idsThatApply(selection, groups, articles, categories),
        ...idsThatApply(selection, asCurrent, articles, categories),
    ])
    return groups.filter((group) => ids.has(group.id))
}

export const statusCounts = (groups: TimeSlotGroup[]): Record<StatusFilter, number> => {
    const counts: Record<StatusFilter, number> = { all: groups.length, active: 0, upcoming: 0, expired: 0 }
    groups.forEach((group) => {
        counts[group.status] += 1
    })
    return counts
}

const STATUS_ORDER: Record<TimeSlotGroupStatus, number> = { active: 0, upcoming: 1, expired: 2 }

export const sortGroups = (groups: TimeSlotGroup[]): TimeSlotGroup[] =>
    [...groups].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || (b.priority ?? 0) - (a.priority ?? 0))
